/**
 * AstaAdminPage — assegnazione giocatori a squadre post-asta.
 *
 * Accesso: Presidente Lega / Admin / Mod Mercato.
 *
 * Workflow:
 * 1. Cerca giocatore (autocomplete sui svincolati)
 * 2. Seleziona squadra di destinazione
 * 3. Inserisci prezzo pagato (M€ fantamilioni)
 * 4. Click "Assegna" → POST /admin/asta-svincolati/registra
 *    - Crea possesso (proprietà) per la squadra
 *    - Movimento budget negativo sulla squadra (acquisto svincolato)
 *
 * Auto-detect: stagione attiva + finestra attiva (necessari per il backend).
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { useSquadre } from "../../hooks/useFase7";
import { useAuthStore } from "../../lib/auth-store";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { SquadraSelector } from "../common/SquadraSelector";
import { sigleRuoli } from "../common/nomenclatura";
import { RoseSvincoloTab } from "./RoseSvincoloTab";
import { tr } from "../../i18n";

const ALLOWED_ROLES = new Set(["presidente_lega", "admin", "mod_mercato"]);

interface GiocatoreLight {
  id: string;
  nome: string;
  club_serie_a: string | null;
  ruolo_classic: string | null;
  ruoli_mantra: string[] | null;
  posseduto_da_squadra_id: string | null;
  valore_tm_milioni?: string | number | null;
}

interface FinestraItem {
  id: string;
  stagione_id: string;
  tipo: "estivo" | "invernale" | "asta_svincolati";
  attiva: boolean;
}

interface AssegnazioneEffettuata {
  asta_id: string;
  giocatore_nome: string;
  squadra_nome: string;
  prezzo_milioni: number;
  ts: number;
}

function userHasAccess(user: ReturnType<typeof useAuth>["user"]): boolean {
  if (!user) return false;
  const roles = new Set<string>([user.ruolo_lega ?? ""]);
  for (const r of user.ruoli_aggiuntivi ?? []) roles.add(r);
  for (const r of roles) if (ALLOWED_ROLES.has(r)) return true;
  return false;
}

export function AstaAdminPage() {
  const { user } = useAuth();
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: stagioneAttiva } = useStagioneAttiva();
  const { data: squadre } = useSquadre();
  const stagioneId = stagioneAttiva?.id ?? null;

  const hasAccess = userHasAccess(user);

  // Tab: form singolo | paste massivo | rose (svincola / sposta)
  const [tab, setTab] = useState<"singolo" | "massivo" | "rose">("singolo");
  // Andrea 2026-10-09: cerca anche chi e' gia' in una squadra (per spostarlo)
  const [ancheAssegnati, setAncheAssegnati] = useState(false);

  // Finestra attiva
  const { data: finestre } = useQuery({
    queryKey: ["stagione-finestre", stagioneId],
    queryFn: async () =>
      (await api.get<FinestraItem[]>(`/admin/stagioni/${stagioneId}/finestre`)).data,
    enabled: hasAccess && !!stagioneId,
  });
  const finestraAttiva = finestre?.find((f) => f.attiva) ?? null;

  // Form state
  const [searchQ, setSearchQ] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [giocatoreSel, setGiocatoreSel] = useState<GiocatoreLight | null>(null);
  const [squadraId, setSquadraId] = useState("");
  const [prezzoStr, setPrezzoStr] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Storico assegnazioni di questa sessione (in-memory, non persistito)
  const [assegnazioni, setAssegnazioni] = useState<AssegnazioneEffettuata[]>([]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchQ.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Search giocatori (solo svincolati)
  const { data: searchResults } = useQuery({
    queryKey: ["giocatori-search", searchDebounced, ancheAssegnati],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("search", searchDebounced);
      if (!ancheAssegnati) params.set("only_svincolati", "true");
      params.set("limit", "20");
      return (await api.get<{ items: GiocatoreLight[] }>(`/giocatori?${params}`)).data.items;
    },
    enabled: isAuth && searchDebounced.length >= 2,
  });

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showDropdown]);

  const assegnaMut = useMutation({
    mutationFn: async () => {
      if (!stagioneId) throw new Error(tr("Nessuna stagione attiva"));
      if (!finestraAttiva) throw new Error(tr("Nessuna finestra mercato attiva"));
      if (!giocatoreSel) throw new Error(tr("Seleziona un giocatore"));
      if (!squadraId) throw new Error(tr("Seleziona la squadra di destinazione"));
      const prezzo = Number(prezzoStr);
      if (!Number.isFinite(prezzo) || prezzo < 0) {
        throw new Error(tr("Prezzo non valido (deve essere >= 0)"));
      }
      await api.post("/admin/asta-svincolati/registra", {
        finestra_id: finestraAttiva.id,
        giocatore_id: giocatoreSel.id,
        squadra_aggiudicataria_id: squadraId,
        prezzo_milioni: prezzo,
        stagione_id: stagioneId,
      });
      return {
        nome: giocatoreSel.nome,
        prezzo,
        squadra_nome: squadre?.find((s) => s.id === squadraId)?.nome ?? "?",
      };
    },
    onSuccess: (data) => {
      setAssegnazioni((prev) => [
        {
          asta_id: crypto.randomUUID(),
          giocatore_nome: data.nome,
          squadra_nome: data.squadra_nome,
          prezzo_milioni: data.prezzo,
          ts: Date.now(),
        },
        ...prev,
      ]);
      // Reset form (mantieni squadra/prezzo per prossima assegnazione rapida)
      setGiocatoreSel(null);
      setSearchQ("");
      setShowDropdown(false);
    },
  });

  // Tool: sincronizza iscrizioni mancanti per i possessi attivi
  const syncIscrizioniMut = useMutation({
    mutationFn: async () => {
      if (!stagioneId) throw new Error(tr("Nessuna stagione attiva"));
      const res = await api.post<{ iscrizioni_create: number }>(
        "/admin/asta-svincolati/sync-iscrizioni",
        { stagione_id: stagioneId },
      );
      return res.data;
    },
    onSuccess: (data) => {
      alert(
        tr("✓ Sync iscrizioni completato.\\n") +
          tr("Iscrizioni create: {{v1}}\\n", { v1: data.iscrizioni_create }) +
          tr("Ora i giocatori comprati dovrebbero apparire in /rose, /formazione, /gestione."),
      );
    },
    onError: (e: { response?: { data?: { detail?: string } }; message?: string }) => {
      alert(
        tr("Errore sync: {{v1}}", { v1: e.response?.data?.detail ?? e.message ?? "sconosciuto" }),
      );
    },
  });

  if (!hasAccess) {
    return (
      <MobileShell logoSuffix="ASTA">
        <h2 className="font-display text-3xl text-accent tracking-wide">
          {tr("Assegna / svincola giocatori")}
        </h2>
        <div className="card text-center p-8">
          <div className="text-4xl mb-2">🔒</div>
          <div className="text-fg-muted">
            {tr("Accesso riservato a Presidente Lega, Admin e Mod Mercato.")}
          </div>
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell logoSuffix="ASTA">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-3xl text-accent tracking-wide">
          {tr("Assegna / svincola giocatori")}
        </h2>
        {stagioneAttiva && (
          <div className="text-sm text-fg-muted">
            {tr("Stagione")} <span className="text-fg font-bold">{stagioneAttiva.anno}</span>
          </div>
        )}
      </div>

      {!stagioneAttiva && (
        <div className="card text-fg-muted text-center p-4">{tr("Nessuna stagione attiva.")}</div>
      )}

      {stagioneAttiva && !finestraAttiva && (
        <div className="card bg-warning/10 border-warning/30 text-sm text-warning">
          {tr(
            "⚠ Nessuna finestra mercato attiva. Assegnazioni, svincoli e spostamenti si fanno solo a mercato aperto: apri il mercato dalla pagina",
          )}{" "}
          <code>{"/mercato"}</code>.
        </div>
      )}

      {/* Tool sync iscrizioni — utile per fixare possessi senza iscrizione
          (bug pre-fix asta). Idempotente. */}
      {stagioneAttiva && (
        <div className="card bg-info/5 border border-info/30 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-fg-muted">
            🔧 <strong className="text-info">{tr("Sync iscrizioni")}</strong>
            {tr(
              ": crea le iscrizioni mancanti per i giocatori posseduti che non appaiono in /rose, /formazione, /gestione. Idempotente.",
            )}
          </div>
          <button
            type="button"
            disabled={syncIscrizioniMut.isPending}
            onClick={() => syncIscrizioniMut.mutate()}
            className="btn-ghost text-sm border border-info/40 text-info hover:bg-info/10 disabled:opacity-50"
          >
            {syncIscrizioniMut.isPending ? tr("Sync…") : tr("Sincronizza iscrizioni da possessi")}
          </button>
        </div>
      )}

      {stagioneAttiva && finestraAttiva && (
        <>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setTab("singolo")}
              className={`pill ${
                tab === "singolo" ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"
              }`}
            >
              {tr("📝 Assegnazione singola")}
            </button>
            <button
              type="button"
              onClick={() => setTab("massivo")}
              className={`pill ${
                tab === "massivo" ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"
              }`}
            >
              {tr("📋 Assegnazione massiva")}
            </button>
            <button
              type="button"
              onClick={() => setTab("rose")}
              className={`pill ${
                tab === "rose" ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"
              }`}
            >
              {tr("🧹 Svincola / sposta / modifica")}
            </button>
          </div>

          {tab === "rose" && (
            <RoseSvincoloTab
              stagioneId={stagioneId!}
              onSposta={(g) => {
                setGiocatoreSel({
                  id: g.id,
                  nome: g.nome,
                  club_serie_a: null,
                  ruolo_classic: null,
                  ruoli_mantra: null,
                  posseduto_da_squadra_id: null,
                  valore_tm_milioni: g.valore_tm_milioni,
                });
                if (g.valore_tm_milioni !== null) setPrezzoStr(String(g.valore_tm_milioni));
                setSquadraId("");
                setTab("singolo");
              }}
            />
          )}

          {tab === "singolo" && (
            <p className="text-xs text-fg-muted">
              {tr(
                "Cerca un giocatore, scegli la squadra di destinazione e il prezzo in fantamilioni: si propone il valore TM, ma puoi scriverlo a mano. Se il giocatore era già in un'altra squadra viene spostato. Il prezzo viene scalato dal budget della squadra.",
              )}
            </p>
          )}

          {tab === "massivo" && (
            <PasteMassivoSection
              stagioneId={stagioneId!}
              finestraId={finestraAttiva.id}
              onDone={(ok) => setAssegnazioni((prev) => [...ok, ...prev])}
            />
          )}

          {tab === "singolo" && (
            <div className="card space-y-3">
              <div ref={wrapRef} className="relative">
                <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                  {tr("Giocatore")}
                </label>
                {giocatoreSel ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-primary-deep border border-accent/40 rounded-lg">
                    <span className="font-bold text-fg">{giocatoreSel.nome}</span>
                    <span className="text-xs text-fg-muted">
                      {giocatoreSel.ruoli_mantra
                        ? sigleRuoli(giocatoreSel.ruoli_mantra)
                        : giocatoreSel.ruolo_classic}
                      {giocatoreSel.club_serie_a
                        ? tr(" · {{v1}}", { v1: giocatoreSel.club_serie_a })
                        : ""}
                    </span>
                    <button
                      onClick={() => setGiocatoreSel(null)}
                      className="ml-auto text-sm text-danger hover:text-danger/70"
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={searchQ}
                      onChange={(e) => {
                        setSearchQ(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      placeholder={tr("Cerca per nome (min. 2 lettere)…")}
                      className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                    {showDropdown && searchDebounced.length >= 2 && (
                      <div className="absolute left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-bg-card border border-primary/30 rounded-lg shadow-card z-10">
                        {!searchResults && (
                          <div className="p-3 text-fg-muted text-xs">{tr("Caricamento…")}</div>
                        )}
                        {searchResults && searchResults.length === 0 && (
                          <div className="p-3 text-fg-muted text-xs">
                            {tr("Nessun giocatore svincolato trovato.")}
                          </div>
                        )}
                        {searchResults?.map((g) => (
                          <button
                            key={g.id}
                            type="button"
                            onClick={() => {
                              setGiocatoreSel(g);
                              setShowDropdown(false);
                              setSearchQ("");
                              // Andrea 2026-10-09: prezzo proposto = valore TM
                              const tm = Number(g.valore_tm_milioni);
                              if (g.valore_tm_milioni != null && Number.isFinite(tm))
                                setPrezzoStr(String(tm));
                            }}
                            className="w-full text-left px-3 py-2 border-b border-primary/10 last:border-b-0 hover:bg-primary-soft/30 text-sm"
                          >
                            <div className="font-bold text-fg">{g.nome}</div>
                            <div className="text-[10px] text-fg-muted">
                              {g.ruoli_mantra ? sigleRuoli(g.ruoli_mantra) : g.ruolo_classic}
                              {g.club_serie_a ? ` · ${g.club_serie_a}` : ""}
                              {g.valore_tm_milioni != null
                                ? tr(" · TM {{v1}}", { v1: Number(g.valore_tm_milioni).toFixed(1) })
                                : ""}
                              {g.posseduto_da_squadra_id
                                ? tr(" · in rosa di {{v1}}", {
                                    v1:
                                      squadre?.find((s) => s.id === g.posseduto_da_squadra_id)
                                        ?.nome ?? "?",
                                  })
                                : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {!giocatoreSel && (
                <label className="flex items-center gap-2 text-xs text-fg-muted">
                  <input
                    type="checkbox"
                    checked={ancheAssegnati}
                    onChange={(e) => setAncheAssegnati(e.target.checked)}
                  />
                  {tr("Cerca anche tra i giocatori già in una squadra (per spostarli)")}
                </label>
              )}

              <SquadraSelector
                value={squadraId}
                onChange={setSquadraId}
                label="Squadra di destinazione"
                autoSelectMine={false}
              />

              <div>
                <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                  {tr("Prezzo (M fantamilioni) — proposto il valore TM, modificabile")}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min="0"
                  value={prezzoStr}
                  onChange={(e) => setPrezzoStr(e.target.value)}
                  placeholder={tr("es. 15.0")}
                  className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>

              <button
                type="button"
                onClick={() => assegnaMut.mutate()}
                disabled={assegnaMut.isPending || !giocatoreSel || !squadraId || !prezzoStr}
                className="btn-primary disabled:opacity-50"
              >
                {assegnaMut.isPending ? tr("Assegno…") : tr("✓ Assegna giocatore")}
              </button>

              {assegnaMut.isError && (
                <div className="text-sm text-danger bg-danger/10 rounded p-2">
                  {(() => {
                    const err = assegnaMut.error as {
                      response?: { data?: { detail?: unknown }; status?: number };
                      message?: string;
                    };
                    const raw = err.response?.data?.detail;
                    if (typeof raw === "string") return raw;
                    if (Array.isArray(raw))
                      return raw
                        .map(
                          (d: { loc?: unknown[]; msg?: string }) =>
                            `${Array.isArray(d.loc) ? d.loc.join(".") : ""}: ${
                              d.msg ?? JSON.stringify(d)
                            }`,
                        )
                        .join("; ");
                    if (raw !== undefined && raw !== null) {
                      try {
                        return JSON.stringify(raw);
                      } catch {
                        return String(raw);
                      }
                    }
                    return err.message ?? tr("errore sconosciuto");
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Storico assegnazioni di questa sessione */}
          {assegnazioni.length > 0 && (
            <div className="card p-0 overflow-hidden">
              <div className="px-4 py-2 bg-primary-deep/40 text-xs uppercase tracking-wider text-fg-muted">
                Assegnazioni effettuate in questa sessione ({assegnazioni.length})
              </div>
              {assegnazioni.map((a) => (
                <div
                  key={a.asta_id}
                  className="flex items-center gap-3 p-3 border-b border-primary/10 last:border-b-0 text-sm"
                >
                  <span className="text-success">✓</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{a.giocatore_nome}</div>
                    <div className="text-[10px] text-fg-muted">→ {a.squadra_nome}</div>
                  </div>
                  <div className="font-mono font-bold text-danger">
                    −{a.prezzo_milioni.toFixed(2)}M
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </MobileShell>
  );
}

// ============================================================
// Paste massivo: 1 riga per assegnazione
// Formato: Nome Giocatore | Nome Squadra | Prezzo
// Supporta separatori: | , ; \t
// ============================================================

interface RigaParsed {
  raw: string;
  nome: string;
  squadra: string;
  prezzo: number;
}

interface RigaProcessata {
  raw: string;
  status: "pending" | "ok" | "error";
  message: string;
  giocatore_nome?: string;
  squadra_nome?: string;
  prezzo?: number;
}

function parseRighe(text: string): { ok: RigaParsed[]; errors: string[] } {
  const ok: RigaParsed[] = [];
  const errors: string[] = [];
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (const raw of lines) {
    // Split su qualsiasi separatore comune
    const parts = raw
      .split(/[|,;\t]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length !== 3) {
      errors.push(
        tr('Formato non valido (servono 3 campi: nome | squadra | prezzo): "{{v1}}"', { v1: raw }),
      );
      continue;
    }
    const [nome, squadra, prezzoStr] = parts;
    const prezzo = Number(prezzoStr.replace(",", "."));
    if (!Number.isFinite(prezzo) || prezzo < 0) {
      errors.push(
        tr('Prezzo non valido ("{{v1}}") nella riga: {{v2}}', { v1: prezzoStr, v2: raw }),
      );
      continue;
    }
    ok.push({ raw, nome, squadra, prezzo });
  }
  return { ok, errors };
}

function PasteMassivoSection({
  stagioneId,
  finestraId,
  onDone,
}: {
  stagioneId: string;
  finestraId: string;
  onDone: (ok: AssegnazioneEffettuata[]) => void;
}) {
  const { data: squadre } = useSquadre();
  const [pasteText, setPasteText] = useState("");
  const [parsed, setParsed] = useState<RigaParsed[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [processed, setProcessed] = useState<RigaProcessata[]>([]);

  function handleParse() {
    const { ok, errors } = parseRighe(pasteText);
    setParsed(ok);
    setParseErrors(errors);
    setProcessed([]);
  }

  function squadraByName(name: string): string | null {
    const norm = (s: string) => s.toLowerCase().trim();
    const target = norm(name);
    const exact = squadre?.find((s) => norm(s.nome) === target);
    if (exact) return exact.id;
    const partial = squadre?.find((s) => norm(s.nome).includes(target));
    return partial?.id ?? null;
  }

  async function findGiocatoreByName(nome: string): Promise<{
    id: string;
    nome: string;
  } | null> {
    const params = new URLSearchParams();
    params.set("search", nome);
    params.set("only_svincolati", "true");
    params.set("limit", "5");
    const res = await api.get<{ items: { id: string; nome: string }[] }>(`/giocatori?${params}`);
    const items = res.data.items;
    if (items.length === 0) return null;
    // Match esatto (case-insensitive) preferito
    const target = nome.toLowerCase().trim();
    const exact = items.find((g) => g.nome.toLowerCase() === target);
    return exact ?? items[0];
  }

  async function processAll() {
    if (parsed.length === 0) return;
    setRunning(true);
    const out: RigaProcessata[] = parsed.map((r) => ({
      raw: r.raw,
      status: "pending",
      message: tr("in coda…"),
    }));
    setProcessed(out);
    const successi: AssegnazioneEffettuata[] = [];

    for (let i = 0; i < parsed.length; i++) {
      const r = parsed[i];
      try {
        const giocatore = await findGiocatoreByName(r.nome);
        if (!giocatore) {
          out[i] = {
            raw: r.raw,
            status: "error",
            message: tr('Giocatore "{{v1}}" non trovato o non svincolato', { v1: r.nome }),
          };
          setProcessed([...out]);
          continue;
        }
        const sqid = squadraByName(r.squadra);
        if (!sqid) {
          out[i] = {
            raw: r.raw,
            status: "error",
            message: tr('Squadra "{{v1}}" non trovata', { v1: r.squadra }),
          };
          setProcessed([...out]);
          continue;
        }
        await api.post("/admin/asta-svincolati/registra", {
          finestra_id: finestraId,
          giocatore_id: giocatore.id,
          squadra_aggiudicataria_id: sqid,
          prezzo_milioni: r.prezzo,
          stagione_id: stagioneId,
        });
        const sqNome = squadre?.find((s) => s.id === sqid)?.nome ?? r.squadra;
        out[i] = {
          raw: r.raw,
          status: "ok",
          message: tr("Assegnato"),
          giocatore_nome: giocatore.nome,
          squadra_nome: sqNome,
          prezzo: r.prezzo,
        };
        successi.push({
          asta_id: crypto.randomUUID(),
          giocatore_nome: giocatore.nome,
          squadra_nome: sqNome,
          prezzo_milioni: r.prezzo,
          ts: Date.now(),
        });
        setProcessed([...out]);
      } catch (e) {
        const err = e as { response?: { data?: { detail?: string } }; message?: string };
        out[i] = {
          raw: r.raw,
          status: "error",
          message: err.response?.data?.detail ?? err.message ?? tr("Errore POST"),
        };
        setProcessed([...out]);
      }
    }
    setRunning(false);
    if (successi.length > 0) onDone(successi);
  }

  const numOk = processed.filter((p) => p.status === "ok").length;
  const numErr = processed.filter((p) => p.status === "error").length;

  return (
    <div className="card space-y-3">
      <div className="text-xs text-fg-muted">
        <strong className="text-fg">{tr("Formato:")}</strong> una riga per assegnazione, 3 campi
        separati da <code>|</code>, <code>;</code>
        {tr(", virgola o tab.")}
        <pre className="text-xs bg-primary-deep p-2 rounded font-mono mt-2">
          {`Nome Giocatore | Nome Squadra | Prezzo
Lautaro Martinez | Inter | 95
Theo Hernandez | Milan | 28
...`}
        </pre>
        {tr("Nome giocatore: usa lo stesso nome che vedi su")} <code>{"/giocatori"}</code>{" "}
        {tr(
          "(match esatto preferito, fallback fuzzy). Solo svincolati. Squadra: match case-insensitive su nome lega.",
        )}
      </div>

      <textarea
        value={pasteText}
        onChange={(e) => setPasteText(e.target.value)}
        rows={10}
        placeholder={"Lautaro Martinez | Inter | 95"}
        className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
      />

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleParse}
          disabled={!pasteText.trim() || running}
          className="btn-ghost text-sm border border-info/40 text-info"
        >
          {tr("📋 Parse")}
        </button>
        <button
          type="button"
          onClick={processAll}
          disabled={parsed.length === 0 || running}
          className="btn-primary text-sm"
        >
          {running
            ? tr("Assegno… ({{v1}}/{{v2}})", {
                v1: processed.filter((p) => p.status !== "pending").length,
                v2: parsed.length,
              })
            : tr("✓ Assegna {{v1}}", { v1: parsed.length || "" })}
        </button>
      </div>

      {parseErrors.length > 0 && (
        <div className="text-xs text-danger bg-danger/10 rounded p-2 space-y-1">
          <div className="font-bold">Errori parsing ({parseErrors.length}):</div>
          {parseErrors.slice(0, 10).map((e, i) => (
            <div key={i}>• {e}</div>
          ))}
        </div>
      )}

      {parsed.length > 0 && processed.length === 0 && (
        <div className="text-xs text-fg-muted">
          {parsed.length} righe pronte. Click "Assegna" per eseguire.
        </div>
      )}

      {processed.length > 0 && (
        <div className="text-xs space-y-1">
          <div className="font-bold">
            {tr("Risultati:")} <span className="text-success">{numOk} OK</span> ·{" "}
            <span className="text-danger">{numErr} errori</span>
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1">
            {processed.map((p, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 p-1.5 rounded font-mono ${
                  p.status === "ok"
                    ? "bg-success/10 text-success"
                    : p.status === "error"
                      ? "bg-danger/10 text-danger"
                      : "bg-fg-muted/10 text-fg-muted"
                }`}
              >
                <span>{p.status === "ok" ? "✓" : p.status === "error" ? "✗" : "…"}</span>
                <span className="flex-1 min-w-0 truncate">{p.raw}</span>
                <span className="text-[10px] truncate max-w-[40%]">{p.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
